import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import type { MessagePayload, Messaging, Unsubscribe } from "firebase/messaging";
import { auth, firebaseApp } from "@/firebase/app";
import { apiRequest, FunctionsRequestError } from "@/services/functionsClient";

export type PushPermissionStatus =
  | "unsupported"
  | "default"
  | "denied"
  | "granted";

export type PushConfigurationStatus = "unknown" | "available" | "unavailable";

export interface ForegroundPushMessage {
  title: string;
  body: string;
  notificationID: string | null;
  kind: string | null;
}

export interface PushNotificationContextValue {
  permissionStatus: PushPermissionStatus;
  configurationStatus: PushConfigurationStatus;
  isEnabling: boolean;
  error: string | null;
  foregroundMessage: ForegroundPushMessage | null;
  enablePush(): Promise<boolean>;
  refreshPermissionStatus(): Promise<void>;
  dismissForegroundMessage(): void;
  prepareForSignOut(): Promise<void>;
}

type MessagingModule = typeof import("firebase/messaging");

interface PushConfigResponse {
  vapidKey: string;
}

const PushNotificationContext = createContext<PushNotificationContextValue | null>(null);

let activeSignOutPreparation: (() => Promise<void>) | null = null;

/** AuthProvider calls this before Firebase sign-out while its ID token is valid. */
export async function preparePushNotificationsForSignOut(): Promise<void> {
  await activeSignOutPreparation?.();
}

function browserPermissionStatus(): PushPermissionStatus {
  if (
    typeof window === "undefined"
    || !("Notification" in window)
    || !("serviceWorker" in navigator)
  ) {
    return "unsupported";
  }
  return Notification.permission;
}

export function foregroundPushMessage(payload: MessagePayload): ForegroundPushMessage {
  return {
    title: payload.notification?.title?.trim() || "SBot",
    body: payload.notification?.body?.trim() || "",
    notificationID: payload.data?.notificationID?.trim() || null,
    kind: payload.data?.kind?.trim() || null,
  };
}

function pushErrorMessage(error: unknown): string {
  if (error instanceof FunctionsRequestError && error.status === 503) {
    return "Push notifications are not configured yet. Notifications remain available here.";
  }
  if (error instanceof Error && error.message) return error.message;
  return "Push notifications could not be enabled. Try again.";
}

async function loadMessaging(): Promise<{
  module: MessagingModule;
  messaging: Messaging;
} | null> {
  const module = await import("firebase/messaging");
  if (!await module.isSupported()) return null;
  return { module, messaging: module.getMessaging(firebaseApp) };
}

async function fetchPushConfig(): Promise<PushConfigResponse> {
  const response = await apiRequest<unknown>("/account/web-push-config");
  if (
    typeof response !== "object"
    || response === null
    || typeof (response as Record<string, unknown>).vapidKey !== "string"
    || !(response as Record<string, unknown>).vapidKey
  ) {
    throw new Error("Push notification configuration is invalid.");
  }
  return response as PushConfigResponse;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register("/firebase-messaging-sw.js", {
    scope: "/",
    updateViaCache: "none",
  });
}

async function registerFID(fid: string): Promise<void> {
  await apiRequest("/account/device-tokens", {
    method: "POST",
    body: {
      token: fid,
      platform: "web",
      registrationType: "fid",
      appVersion: __APP_BUILD_ID__,
    },
  });
}

async function removeFID(fid: string): Promise<void> {
  await apiRequest("/account/device-tokens/remove", {
    method: "POST",
    body: {
      token: fid,
      registrationType: "fid",
    },
  });
}

export function PushNotificationProvider({ children }: { children: ReactNode }) {
  const [permissionStatus, setPermissionStatus] = useState<PushPermissionStatus>(
    browserPermissionStatus,
  );
  const [configurationStatus, setConfigurationStatus] =
    useState<PushConfigurationStatus>("unknown");
  const [isEnabling, setIsEnabling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foregroundMessage, setForegroundMessage] =
    useState<ForegroundPushMessage | null>(null);

  const currentUserRef = useRef<User | null>(auth.currentUser);
  const messagingRef = useRef<Messaging | null>(null);
  const messagingModuleRef = useRef<MessagingModule | null>(null);
  const latestFIDRef = useRef<string | null>(null);
  const lastUploadedKeyRef = useRef<string | null>(null);
  const registerPromiseRef = useRef<Promise<void> | null>(null);
  const listenersInstalledRef = useRef(false);
  const listenerUnsubscribesRef = useRef<Unsubscribe[]>([]);

  const uploadFID = useCallback(async (fid: string) => {
    latestFIDRef.current = fid;
    const user = currentUserRef.current;
    if (!user) return;

    const uploadKey = `${user.uid}|${fid}`;
    if (lastUploadedKeyRef.current === uploadKey) return;

    await registerFID(fid);
    lastUploadedKeyRef.current = uploadKey;
  }, []);

  const removeKnownFID = useCallback(async (fid: string) => {
    if (!currentUserRef.current) return;
    try {
      await removeFID(fid);
    } catch {
      // Best-effort. Server-side pruning handles stale registrations.
    }
    if (latestFIDRef.current === fid) latestFIDRef.current = null;
    lastUploadedKeyRef.current = null;
  }, []);

  const installMessagingListeners = useCallback((
    module: MessagingModule,
    messaging: Messaging,
  ) => {
    if (listenersInstalledRef.current) return;
    listenersInstalledRef.current = true;
    listenerUnsubscribesRef.current = [
      module.onRegistered(messaging, (fid) => {
        void uploadFID(fid).catch((uploadError: unknown) => {
          setError(pushErrorMessage(uploadError));
        });
      }),
      module.onUnregistered(messaging, (fid) => {
        void removeKnownFID(fid);
      }),
      module.onMessage(messaging, (payload) => {
        setForegroundMessage(foregroundPushMessage(payload));
      }),
    ];
  }, [removeKnownFID, uploadFID]);

  const registerGrantedSession = useCallback(async () => {
    if (browserPermissionStatus() !== "granted" || !currentUserRef.current) return;
    if (registerPromiseRef.current) return registerPromiseRef.current;

    const registration = (async () => {
      const loaded = await loadMessaging();
      if (!loaded) {
        setPermissionStatus("unsupported");
        return;
      }

      messagingModuleRef.current = loaded.module;
      messagingRef.current = loaded.messaging;
      installMessagingListeners(loaded.module, loaded.messaging);

      const [{ vapidKey }, serviceWorkerRegistration] = await Promise.all([
        fetchPushConfig(),
        registerServiceWorker(),
      ]);
      setConfigurationStatus("available");
      await loaded.module.register(loaded.messaging, {
        vapidKey,
        serviceWorkerRegistration,
      });
    })().catch((registrationError: unknown) => {
      if (registrationError instanceof FunctionsRequestError && registrationError.status === 503) {
        setConfigurationStatus("unavailable");
      }
      throw registrationError;
    }).finally(() => {
      registerPromiseRef.current = null;
    });

    registerPromiseRef.current = registration;
    return registration;
  }, [installMessagingListeners]);

  const refreshPermissionStatus = useCallback(async () => {
    const status = browserPermissionStatus();
    if (status === "unsupported") {
      setPermissionStatus("unsupported");
      return;
    }

    const loaded = await loadMessaging();
    const nextStatus = loaded ? Notification.permission : "unsupported";
    setPermissionStatus(nextStatus);
  }, []);

  const enablePush = useCallback(async (): Promise<boolean> => {
    setIsEnabling(true);
    setError(null);
    try {
      const loaded = await loadMessaging();
      if (!loaded) {
        setPermissionStatus("unsupported");
        return false;
      }

      const permission = Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();
      setPermissionStatus(permission);
      if (permission !== "granted") return false;

      await registerGrantedSession();
      return true;
    } catch (enableError) {
      setError(pushErrorMessage(enableError));
      return false;
    } finally {
      setIsEnabling(false);
    }
  }, [registerGrantedSession]);

  const prepareForSignOut = useCallback(async () => {
    const fid = latestFIDRef.current;
    if (fid) await removeKnownFID(fid);

    const module = messagingModuleRef.current;
    const messaging = messagingRef.current;
    if (module && messaging) {
      try {
        await module.unregister(messaging);
      } catch {
        // Best-effort. A later send prunes stale registrations.
      }
    }
    latestFIDRef.current = null;
    lastUploadedKeyRef.current = null;
  }, [removeKnownFID]);

  useEffect(() => {
    activeSignOutPreparation = prepareForSignOut;
    return () => {
      if (activeSignOutPreparation === prepareForSignOut) {
        activeSignOutPreparation = null;
      }
    };
  }, [prepareForSignOut]);

  useEffect(() => onAuthStateChanged(auth, (user) => {
    const priorUID = currentUserRef.current?.uid;
    currentUserRef.current = user;
    if (user?.uid !== priorUID) lastUploadedKeyRef.current = null;

    if (user && browserPermissionStatus() === "granted") {
      void registerGrantedSession().catch((registrationError: unknown) => {
        setError(pushErrorMessage(registrationError));
      });
    }
  }), [registerGrantedSession]);

  useEffect(() => {
    const handleFocus = () => {
      void refreshPermissionStatus();
      if (currentUserRef.current && browserPermissionStatus() === "granted") {
        void registerGrantedSession().catch((registrationError: unknown) => {
          setError(pushErrorMessage(registrationError));
        });
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refreshPermissionStatus, registerGrantedSession]);

  useEffect(() => () => {
    for (const unsubscribe of listenerUnsubscribesRef.current) unsubscribe();
    listenerUnsubscribesRef.current = [];
    listenersInstalledRef.current = false;
  }, []);

  const value = useMemo<PushNotificationContextValue>(() => ({
    permissionStatus,
    configurationStatus,
    isEnabling,
    error,
    foregroundMessage,
    enablePush,
    refreshPermissionStatus,
    dismissForegroundMessage: () => setForegroundMessage(null),
    prepareForSignOut,
  }), [
    configurationStatus,
    enablePush,
    error,
    foregroundMessage,
    isEnabling,
    permissionStatus,
    prepareForSignOut,
    refreshPermissionStatus,
  ]);

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export function usePushNotifications(): PushNotificationContextValue {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error("usePushNotifications must be used within PushNotificationProvider.");
  }
  return context;
}
